import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_CASE_STUDIES = [
  {
    id: '1',
    slug: 'miami-seawall-restoration',
    title: 'Miami Beach Seawall Restoration',
    location: 'Miami Beach, FL',
    projectType: 'Marine/Coastal',
    product: 'GEObar B',
    quantity: '45,000 linear feet',
    year: '2025',
    summary: 'Complete seawall reinforcement using BFRP rebar to combat saltwater corrosion in South Florida\'s aggressive marine environment.',
    challenge: 'Existing steel-reinforced seawall showed severe corrosion after only 18 years. Rising sea levels and increasing storm intensity required a 100+ year solution.',
    solution: 'Specified GEObar B BFRP rebar for complete corrosion immunity. Reduced concrete cover requirements and eliminated need for epoxy-coated alternatives.',
    results: ['100+ year projected service life', 'Zero corrosion maintenance required', '40% reduction in lifecycle cost', 'Faster installation due to lighter weight'],
    image: 'https://images.unsplash.com/photo-1590496793929-36417d3117de?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: '2',
    slug: 'hospital-mri-wing',
    title: 'Regional Hospital MRI Wing',
    location: 'Houston, TX',
    projectType: 'Healthcare',
    product: 'GEObar FG',
    quantity: '12,000 linear feet',
    year: '2025',
    summary: 'Non-magnetic GFRP reinforcement for new MRI facility requiring zero electromagnetic interference with sensitive imaging equipment.',
    challenge: 'New MRI wing required completely non-magnetic structural reinforcement within 25 feet of three MRI machines. Steel rebar would interfere with imaging.',
    solution: 'GEObar FG GFRP rebar provided structural reinforcement without any magnetic properties. Allowed MRI equipment to operate without interference.',
    results: ['Zero electromagnetic interference', 'Full structural code compliance', 'Cost savings vs stainless alternatives', 'Simplified construction sequencing'],
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=800&q=80'
  },
  {
    id: '3',
    slug: 'parking-structure-renovation',
    title: 'Downtown Parking Structure',
    location: 'Chicago, IL',
    projectType: 'Parking Structure',
    product: 'GEObar B',
    quantity: '28,000 linear feet',
    year: '2024',
    summary: 'Renovation of deteriorating parking structure in harsh freeze-thaw environment with heavy de-icing salt exposure.',
    challenge: 'Original steel rebar failed after 22 years due to chloride penetration from de-icing salts. Structure required deck replacement with long-term solution.',
    solution: 'Complete deck replacement using GEObar B BFRP rebar. Composite reinforcement immune to chloride attack from road salts.',
    results: ['Eliminated corrosion failure mode', '75-year extended service life', 'Reduced future maintenance budget', 'Minimal traffic disruption'],
    image: 'https://images.unsplash.com/photo-1545179605-1296651e9d43?auto=format&fit=crop&w=800&q=80'
  },
];

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState(FALLBACK_CASE_STUDIES);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/case-studies`);
        if (response.data.caseStudies?.length > 0) {
          setCaseStudies(response.data.caseStudies);
        }
      } catch (error) {
        console.log('Using fallback case studies');
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  const projectTypes = ['All', ...new Set(caseStudies.map(cs => cs.projectType))];
  const filteredStudies = activeFilter === 'All' 
    ? caseStudies 
    : caseStudies.filter(cs => cs.projectType === activeFilter);

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="case-studies-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <Building2 size={24} className="text-gold" />
              <span className="tech-label">//PROJECT_PORTFOLIO</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              Case <span className="text-gold">Studies</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Real-world projects demonstrating the performance of composite 
              reinforcement in demanding applications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-4">
            {projectTypes.map(type => (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                className={`px-4 py-2 text-mono text-xs transition-all ${
                  activeFilter === type
                    ? 'bg-gold text-charcoal'
                    : 'border border-border-dark text-stone/60 hover:border-gold/30'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredStudies.map((study, index) => (
                <motion.article
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group border border-border-dark hover:border-gold/30 transition-all bg-surface-dark/30"
                  data-testid={`case-study-${study.id}`}
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-mono text-xs text-gold/70">{study.projectType}</span>
                      <h2 className="heading-section text-stone text-2xl mt-1">{study.title}</h2>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-stone/40 text-sm mb-4">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} />
                        <span>{study.location}</span>
                      </div>
                      <span>•</span>
                      <span>{study.year}</span>
                    </div>

                    <p className="text-stone/60 mb-6">{study.summary}</p>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <span className="text-mono text-xs text-gold/60 block">Product</span>
                        <span className="text-stone text-sm">{study.product}</span>
                      </div>
                      <div>
                        <span className="text-mono text-xs text-gold/60 block">Quantity</span>
                        <span className="text-stone text-sm">{study.quantity}</span>
                      </div>
                    </div>

                    <div className="border-t border-border-dark pt-4">
                      <span className="text-mono text-xs text-gold/60 block mb-2">Key Results</span>
                      <ul className="space-y-1">
                        {study.results.slice(0, 2).map((result, i) => (
                          <li key={i} className="text-stone/50 text-sm flex items-center gap-2">
                            <span className="w-1 h-1 bg-gold rounded-full" />
                            {result}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 border-t border-border-dark bg-surface-dark/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="heading-display text-stone text-3xl mb-4">Start Your Project</h2>
          <p className="text-stone/50 mb-8">
            Let's discuss how composite reinforcement can solve your infrastructure challenges.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Contact Us
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
